import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Loader2, MapPin, Navigation, Search, Crosshair } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  GOOGLE_MAPS_LIBRARIES,
  getCachedGoogleMapsKey,
  useBeraMapsLoader,
  loadGoogleMapsKey,
} from "@/lib/googleMapsLoader";
import { DEFAULT_MAP_OPTIONS, getBeraMarkerIcon } from "@/lib/googleMapStyles";

import { tAuto } from "@/lib/tAuto";
const CITY_CENTERS: Record<string, { lat: number; lng: number }> = {
  Luanda: { lat: -8.8368, lng: 13.2343 },
  Benguela: { lat: -12.5763, lng: 13.4055 },
  Lobito: { lat: -12.3644, lng: 13.5456 },
  Huambo: { lat: -12.776, lng: 15.7393 },
  Lubango: { lat: -14.9189, lng: 13.4925 },
  Namibe: { lat: -15.1961, lng: 12.1522 },
};

const ANGOLA_CENTER = { lat: -12.5, lng: 17.5 };

const containerStyle = { width: "100%", height: "100%" };

export type LocationPickerValue = {
  latitude: number;
  longitude: number;
  neighborhood?: string;
  formatted_address?: string;
};

interface Props {
  initialLat?: number | null;
  initialLng?: number | null;
  province?: string;
  city?: string;
  address?: string;
  onChange: (value: LocationPickerValue) => void;
  height?: string;
}

const LocationPicker = ({
  initialLat,
  initialLng,
  province,
  city,
  address,
  onChange,
  height = "260px",
}: Props) => {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState<string | null>(getCachedGoogleMapsKey());
  const [marker, setMarker] = useState<{ lat: number; lng: number } | null>(
    initialLat != null && initialLng != null ? { lat: initialLat, lng: initialLng } : null,
  );
  const [searchInput, setSearchInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (apiKey) return;
    loadGoogleMapsKey().then((k) => k && setApiKey(k));
  }, [apiKey]);

  const { isLoaded } = useBeraMapsLoader(apiKey);

  const center = useMemo(() => {
    if (marker) return marker;
    if (city && CITY_CENTERS[city]) return CITY_CENTERS[city];
    return ANGOLA_CENTER;
  }, [marker, city]);

  const reverseGeocode = useCallback(
    async (lat: number, lng: number): Promise<{ neighborhood?: string; formatted_address?: string }> => {
      if (!isLoaded || !(window as any).google?.maps?.Geocoder) return {};
      try {
        const geocoder = new window.google.maps.Geocoder();
        const res = await geocoder.geocode({ location: { lat, lng } });
        const first = res.results?.[0];
        if (!first) return {};
        const comp = first.address_components || [];
        const find = (type: string) =>
          comp.find((c) => c.types.includes(type))?.long_name;
        return {
          neighborhood:
            find("sublocality") ||
            find("sublocality_level_1") ||
            find("neighborhood") ||
            find("administrative_area_level_3"),
          formatted_address: first.formatted_address,
        };
      } catch {
        return {};
      }
    },
    [isLoaded],
  );

  const placeMarker = useCallback(
    async (lat: number, lng: number) => {
      setMarker({ lat, lng });
      const extra = await reverseGeocode(lat, lng);
      onChange({ latitude: lat, longitude: lng, ...extra });
      mapRef.current?.panTo({ lat, lng });
    },
    [onChange, reverseGeocode],
  );

  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      placeMarker(e.latLng.lat(), e.latLng.lng());
    },
    [placeMarker],
  );

  const handleMarkerDragEnd = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      placeMarker(e.latLng.lat(), e.latLng.lng());
    },
    [placeMarker],
  );

  const handleSearch = useCallback(async () => {
    if (!isLoaded || !(window as any).google?.maps?.Geocoder) return;
    const q = (searchInput || address || "").trim();
    if (!q) return;
    setSearching(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const composed = [q, city, province, "Angola"].filter(Boolean).join(", ");
      const res = await geocoder.geocode({
        address: composed,
        componentRestrictions: { country: "AO" },
      });
      const first = res.results?.[0];
      if (first?.geometry?.location) {
        const lat = first.geometry.location.lat();
        const lng = first.geometry.location.lng();
        await placeMarker(lat, lng);
        mapRef.current?.setZoom(17);
      }
    } catch {
      /* ignore */
    } finally {
      setSearching(false);
    }
  }, [isLoaded, searchInput, address, city, province, placeMarker]);

  const handleUseMyLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        placeMarker(pos.coords.latitude, pos.coords.longitude).finally(() => {
          setLocating(false);
          mapRef.current?.setZoom(17);
        });
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }, [placeMarker]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const markerIcon = useMemo(() => (isLoaded ? getBeraMarkerIcon(44) : undefined), [isLoaded]);

  return (
    <div className={cn("space-y-2", height === "100%" ? "flex flex-col h-full" : "")}>
      <div className={cn("flex items-center gap-2", height === "100%" ? "shrink-0" : "")}>
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
            placeholder={address || "Rua, bairro ou referência…"}
            className="h-10 pl-9 rounded-xl text-sm"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSearch}
          disabled={!isLoaded || searching}
          className="h-10 rounded-xl"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : t("explore.searchPlaceholder") ? "Procurar" : "Procurar"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleUseMyLocation}
          disabled={!isLoaded || locating}
          className="h-10 rounded-xl gap-1.5"
          title={tAuto("auto.components_property_locationpicker.1", "Usar minha localização")}
        >
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
        </Button>
      </div>

      <div
        className={cn(
          "relative w-full rounded-2xl overflow-hidden border border-border/40 bg-muted",
          height === "100%" ? "flex-1" : ""
        )}
        style={height !== "100%" ? { height } : undefined}
      >
        {!isLoaded ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={marker ? 16 : city && CITY_CENTERS[city] ? 12 : 6}
            options={{ ...DEFAULT_MAP_OPTIONS, gestureHandling: "greedy" }}
            onLoad={onLoad}
            onClick={handleMapClick}
          >
            {marker && (
              <Marker
                position={marker}
                draggable
                icon={markerIcon}
                onDragEnd={handleMarkerDragEnd}
              />
            )}
          </GoogleMap>
        )}

        {!marker && isLoaded && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-background/95 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium shadow-md pointer-events-none flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-primary" />
            Toque no mapa para marcar o imóvel
          </div>
        )}
      </div>

      {marker && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Navigation className="w-3.5 h-3.5 text-primary" />
          <span>
            Pin: {marker.lat.toFixed(5)}, {marker.lng.toFixed(5)} · arraste para ajustar
          </span>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
